import { useQuery } from "@tanstack/react-query"

import {
  OrganizationPublicConfig,
  PublicDisplaySettings,
} from "@tfg/types/endpoints/agec"
import { PublicResponses } from "@tfg/types/endpoints/public"

import { publicHttpClient } from "~/backend/axios"

export const usePublicCompanyQuery = (organizationSlug: string) => {
  return useQuery<OrganizationPublicConfig>({
    queryKey: ["public", "organization", organizationSlug],

    queryFn: async () => {
      const response = await publicHttpClient.get(`public/${organizationSlug}`)
      return response.data
    },
  })
}

export const usePublicDisplaySettingsQuery = (slug: string) => {
  return useQuery<PublicDisplaySettings>({
    queryKey: ["public", "display-settings", slug],

    queryFn: async () => {
      const response = await publicHttpClient.get(`public/${slug}`)
      return response.data
    },
  })
}

export const usePublicProductsQuery = (
  productSheetGroupSlug: string | undefined,
  searchParams: URLSearchParams
) =>
  useQuery<PublicResponses.GetProductsCard>({
    queryKey: ["public", "products", productSheetGroupSlug, ...searchParams],
    enabled: !!productSheetGroupSlug,
    queryFn: async () => {
      const params = Object.fromEntries(searchParams)
      const response = await publicHttpClient.get(
        `public/${productSheetGroupSlug}/products`,
        {
          params,
        }
      )
      return response.data
    },
  })

/**
 * Extracts the primary domain from a hostname, handling multi-level TLDs
 * @param hostname - The hostname to extract the primary domain from
 * @returns The primary domain (e.g., "traceforgood.com" from "app.traceforgood.com")
 */
const extractPrimaryDomain = (hostname: string): string => {
  // Split the hostname into parts
  const parts = hostname.split(".")

  // If we have less than 2 parts, return the hostname as is
  if (parts.length < 2) {
    return hostname
  }

  // For most cases, the primary domain is the last 2 parts
  // But we need to handle multi-level TLDs like .co.uk, .com.au, etc.

  // Common multi-level TLDs that we know about
  const multiLevelTlds = [
    ".co.uk",
    ".com.au",
    ".co.za",
    ".co.jp",
    ".co.in",
    ".co.kr",
    ".com.br",
    ".com.mx",
    ".com.sg",
    ".com.hk",
    ".com.tw",
    ".org.uk",
    ".net.uk",
    ".gov.uk",
    ".ac.uk",
    ".org.au",
    ".net.au",
    ".gov.au",
    ".edu.au",
    ".org.za",
    ".net.za",
    ".gov.za",
    ".ne.jp",
    ".or.jp",
    ".go.jp",
    ".ac.jp",
    ".or.kr",
    ".go.kr",
    ".ac.kr",
    ".org.br",
    ".net.br",
    ".gov.br",
    ".org.mx",
    ".net.mx",
    ".gov.mx",
    ".org.sg",
    ".net.sg",
    ".gov.sg",
    ".org.hk",
    ".net.hk",
    ".gov.hk",
    ".org.tw",
    ".net.tw",
    ".gov.tw",
  ]

  // Check if the hostname ends with a known multi-level TLD
  const hostnameLower = hostname.toLowerCase()
  for (const tld of multiLevelTlds) {
    if (hostnameLower.endsWith(tld)) {
      // For multi-level TLDs, we need 3 parts minimum
      if (parts.length >= 3) {
        return parts.slice(-3).join(".")
      }
    }
  }

  // For standard TLDs, return the last 2 parts
  return parts.slice(-2).join(".")
}

export const useProductSheetUrl = (url: string) => {
  const productSheetUrl = new URL(url)
  const currentUrl = new URL(window.location.href)

  const productPrimaryDomain = extractPrimaryDomain(productSheetUrl.hostname)
  const currentPrimaryDomain = extractPrimaryDomain(currentUrl.hostname)

  if (productPrimaryDomain === currentPrimaryDomain) {
    return productSheetUrl.toString()
  }
  const newUrl = new URL(productSheetUrl.pathname, currentUrl.origin)
  return newUrl.toString()
}
